/**
 * CoverageReport (spec §9) — entity × action heatmap + domain coverage gauges.
 *
 * Rows = entities (sorted by hit count desc), cols = canonical scenarios.
 * Cell color scales from dark (0 hits) to domain color (many hits).
 * Dark zone list shows entities never touched. Suggest Actions button
 * calls onSuggest(entityId) to seed the ScenarioBuilder.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import type { CoverageSnapshot, ScenarioAction } from '../state/simulator.js';
import { entityById, domainById, computeDomainStats } from '../state/selectors.js';
import { DomainBadge } from './DomainBadge.js';
import { ConnectivityPill } from './ConnectivityPill.js';
import { EntityChip } from '../components/EntityChip.js';
import { navigateToEntity } from '../state/hashRouter.js';

export interface CoverageReportProps {
  manifest: AtlasManifest;
  coverage: CoverageSnapshot;
  scenarios: readonly ScenarioAction[];
  onSuggest?: (entityId: string) => void;
}

function domainColorVar(manifest: AtlasManifest, domainId: string): string {
  const domain = domainById(manifest, domainId);
  if (!domain) return 'var(--text3)';
  const token = domain.color.startsWith('--') ? domain.color : `--${domain.color}`;
  return `var(${token})`;
}

function cellOpacity(count: number, maxCount: number): number {
  if (maxCount === 0) return 0;
  return Math.max(0.08, count / maxCount);
}

const CELL_W = 18;
const CELL_H = 14;
const LABEL_W = 120;
const HEADER_H = 90;
const GAUGE_R = 18;

export function CoverageReport({ manifest, coverage, scenarios, onSuggest }: CoverageReportProps): React.JSX.Element {
  const topEntities = coverage.entityHits.slice(0, 30);
  const maxHit = topEntities.reduce((m, e) => Math.max(m, e.count), 0);
  const statsMap = new Map(computeDomainStats(manifest).map((s) => [s.domainId, s]));

  const gridW = LABEL_W + scenarios.length * CELL_W + 24;
  const gridH = HEADER_H + topEntities.length * CELL_H + 16;

  return (
    <div className="coverage-report">
      {/* Heatmap grid */}
      <div className="coverage-heatmap" style={{ overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${gridW} ${gridH}`}
          style={{ width: Math.max(gridW, 400), height: gridH }}
          aria-label="Entity coverage heatmap"
        >
          {/* Scenario column headers (rotated) */}
          {scenarios.map((s, si) => (
            <text
              key={s.id}
              x={LABEL_W + si * CELL_W + CELL_W / 2}
              y={HEADER_H - 4}
              textAnchor="start"
              fontSize={8}
              fill="var(--text3)"
              transform={`rotate(-55, ${LABEL_W + si * CELL_W + CELL_W / 2}, ${HEADER_H - 4})`}
              className="coverage-col-label mono"
            >
              {s.label.length > 18 ? s.label.slice(0, 17) : s.label}
            </text>
          ))}

          {/* Entity rows */}
          {topEntities.map(({ entityId, count }, ri) => {
            const entity = entityById(manifest, entityId);
            const color = entity ? domainColorVar(manifest, entity.domain) : 'var(--text3)';
            const y = HEADER_H + ri * CELL_H;

            return (
              <g key={entityId} className="coverage-row">
                {/* Entity label */}
                <text
                  x={LABEL_W - 4}
                  y={y + CELL_H - 3}
                  textAnchor="end"
                  fontSize={9}
                  fill={count > 0 ? 'var(--text1)' : 'var(--text3)'}
                  className="coverage-entity-label mono"
                  onClick={() => navigateToEntity(entityId)}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Navigate to ${entityId}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigateToEntity(entityId);
                    }
                  }}
                >
                  {entityId.length > 16 ? `${entityId.slice(0, 15)}…` : entityId}
                </text>

                {/* Cells — one per scenario */}
                {scenarios.map((s, si) => {
                  const hitInScenario = s.writes.includes(entityId) ? count : 0;
                  return (
                    <rect
                      key={s.id}
                      x={LABEL_W + si * CELL_W + 1}
                      y={y + 1}
                      width={CELL_W - 2}
                      height={CELL_H - 2}
                      rx={2}
                      fill={color}
                      opacity={cellOpacity(hitInScenario, maxHit)}
                      className="coverage-cell"
                    />
                  );
                })}

                {/* Hit count badge */}
                <text
                  x={LABEL_W + scenarios.length * CELL_W + 6}
                  y={y + CELL_H - 3}
                  fontSize={8}
                  fill="var(--text3)"
                  className="mono"
                >
                  {count}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Domain coverage gauges */}
      <div className="coverage-gauges">
        <h4 className="coverage-section-title">Domain Coverage</h4>
        <div className="coverage-gauges-grid">
          {coverage.domainCoverage.map(({ domainId, percent, hitCount, totalCount }) => {
            const color = domainColorVar(manifest, domainId);
            const domain = domainById(manifest, domainId);
            const pct = Math.round(percent * 100);
            const circumference = 2 * Math.PI * GAUGE_R;
            const dash = (pct / 100) * circumference;

            return (
              <div key={domainId} className="coverage-gauge" title={`${domain?.name ?? domainId}: ${hitCount}/${totalCount}`}>
                <svg width={GAUGE_R * 2 + 8} height={GAUGE_R * 2 + 8}>
                  <circle
                    cx={GAUGE_R + 4}
                    cy={GAUGE_R + 4}
                    r={GAUGE_R}
                    fill="none"
                    stroke="var(--surface3)"
                    strokeWidth={4}
                  />
                  <circle
                    cx={GAUGE_R + 4}
                    cy={GAUGE_R + 4}
                    r={GAUGE_R}
                    fill="none"
                    stroke={color}
                    strokeWidth={4}
                    strokeDasharray={`${dash} ${circumference - dash}`}
                    strokeLinecap="round"
                    transform={`rotate(-90, ${GAUGE_R + 4}, ${GAUGE_R + 4})`}
                  />
                  <text
                    x={GAUGE_R + 4}
                    y={GAUGE_R + 8}
                    textAnchor="middle"
                    fontSize={8}
                    fill={color}
                    fontWeight={700}
                  >
                    {pct}%
                  </text>
                </svg>
                <div className="coverage-gauge-label">
                  <DomainBadge domainId={domainId} size="sm" />
                  <ConnectivityPill status={statsMap.get(domainId)?.connectivity ?? 'isolated'} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary stat */}
      <p className="coverage-summary">
        Total entity coverage:{' '}
        <strong>{Math.round(coverage.totalEntityCoverage * 100)}%</strong>
        {' '}({coverage.entityHits.filter((e) => e.count > 0).length} / {manifest.entities.length} entities)
      </p>

      {/* Dark zones */}
      {coverage.darkZones.length > 0 && (
        <div className="coverage-dark-zones">
          <h4 className="coverage-section-title">Dark Zones — Never Written</h4>
          <ul className="coverage-dark-list">
            {coverage.darkZones.map((entityId) => {
              const entity = entityById(manifest, entityId);
              const color = entity ? domainColorVar(manifest, entity.domain) : 'var(--text3)';
              return (
                <li key={entityId} className="coverage-dark-item">
                  <span className="coverage-dark-dot" style={{ background: color }} />
                  <span className="mono coverage-dark-name">
                    <EntityChip entityId={entityId} manifest={manifest} size="sm" />
                  </span>
                  {onSuggest && (
                    <button
                      type="button"
                      className="btn btn-ghost coverage-suggest-btn"
                      onClick={() => onSuggest(entityId)}
                      aria-label={`Suggest scenario for ${entityId}`}
                    >
                      Suggest Action
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
